import { CardFaceData, CardType } from '../../../global-types';
import {
  CardPreviewState,
  CardPreviewSurface,
  CardPreviewTemplateFace,
  CreateCardPreviewStateOptions,
} from '../card-preview.types';
import { createPreviewSnapshot, PreviewableCanvas } from './create-preview-snapshot';
import { getPreviewSurfaceCrop } from './get-preview-surface-crop';

/**
 * Gets the unscaled source dimensions used when exporting S&S canvases.
 *
 * S&S editor canvases can be displayed at fractional zoomed sizes, while their
 * background art is source-sized. Exporting at source dimensions avoids
 * fractional viewport seams in quick preview.
 *
 * @param dimensions Current card face dimensions from card state
 * @returns Whole-pixel source dimensions for S&S snapshot export
 */
const getSignAndSendSnapshotDimensions = (dimensions: CardFaceData['dimensions']): CardFaceData['dimensions'] => {
  const width = Math.ceil(dimensions.width);
  const height = Math.ceil(dimensions.height);
  return { width, height };
};

/**
 * Maps normalized preview surface types to user-facing labels.
 *
 * @param type Normalized preview surface type
 * @returns Label used for the surface and its navigation item
 */
const getSurfaceLabel = (type: string): string => {
  if (type === 'front') return 'Cover';
  if (type === 'inside-left') return 'Inside Left';
  if (type === 'inside-right') return 'Inside Right';
  if (type === 'inside') return 'Inside';
  if (type === 'back') return 'Back';
  return type;
};

/**
 * Normalizes S&S panel types by explicit face type or original face index.
 *
 * Current S&S editor data may already expose only Cover and Inside Right. In
 * that case keep the explicit type; older/generic template data still falls
 * back to the production S&S preview index order.
 *
 * @param type Face type from card state/template data
 * @param index Original face index in cardFacesList/template data
 * @returns Normalized S&S preview type when the index is known
 */
const getSignAndSendSurfaceType = (type: string, index: number): string | undefined => {
  if (type === 'front' || type === 'inside-left' || type === 'inside-right') {
    return type;
  }
  if (index === 0) return 'front';
  if (index === 1) return 'inside-left';
  if (index === 2) return 'inside-right';
  return undefined;
};

/**
 * Gets the quick-preview surface type for POD/DG/S&S.
 *
 * S&S uses index-based panel naming; other card types keep the face type from
 * card state/template data.
 *
 * @param type Face type from card state/template data
 * @param index Original face index
 * @param projectTypeCode Project type code from initialized data
 * @returns Normalized preview surface type
 */
const getPreviewSurfaceType = (type: string, index: number, projectTypeCode: string | undefined): string =>
  projectTypeCode === CardType.SAS ? getSignAndSendSurfaceType(type, index) ?? type : type;

/**
 * Creates one quick-preview surface from a live editor card face.
 *
 * This is the adapter between editor state and the lightweight preview model:
 * it asks Fabric for a snapshot, applies S&S-specific export options, and adds
 * metadata needed by the React renderer.
 *
 * @param face Live card face from cardFacesList
 * @param index Original face index
 * @param projectTypeCode Project type code from initialized data
 * @returns Preview surface, or undefined when no image is available
 */
const createSurfaceFromCardFace = (
  face: CardFaceData,
  index: number,
  projectTypeCode: string | undefined,
): CardPreviewSurface | undefined => {
  const canvas = face.canvas?.current as PreviewableCanvas | undefined;
  const snapshot =
    projectTypeCode === CardType.SAS
      ? createPreviewSnapshot(canvas, {
          exportDimensions: getSignAndSendSnapshotDimensions(face.dimensions),
          resetViewport: true,
        })
      : createPreviewSnapshot(canvas);
  const dataURL = snapshot?.dataURL ?? face.backgroundImage;
  if (!dataURL) {
    return undefined;
  }

  const dimensions =
    snapshot?.dimensions ??
    (projectTypeCode === CardType.SAS ? getSignAndSendSnapshotDimensions(face.dimensions) : face.dimensions);
  const type = getPreviewSurfaceType(face.type, index, projectTypeCode);
  const crop = projectTypeCode === CardType.SAS ? undefined : getPreviewSurfaceCrop(type, dimensions, face.dimensions);
  const trimTransparentEdges = projectTypeCode === CardType.SAS || undefined;

  return {
    dataURL,
    dimensions,
    id: String(face.faceId),
    label: getSurfaceLabel(type),
    navigationLabel: getSurfaceLabel(type),
    type,
    crop,
    trimTransparentEdges,
  };
};

/**
 * Creates one quick-preview surface from template data.
 *
 * Template surfaces are used by Storybook/layout demos or as fallback data
 * when no live Fabric canvas exists; they cannot include current user edits.
 *
 * @param face Template face from initialized project data
 * @param index Original template face index
 * @param projectTypeCode Project type code from initialized data
 * @returns Preview surface, or undefined when the template has no background
 */
const createSurfaceFromTemplateFace = (
  face: CardPreviewTemplateFace,
  index: number,
  projectTypeCode: string | undefined,
): CardPreviewSurface | undefined => {
  if (!face.BackgroundUrl) return undefined;
  const type = getPreviewSurfaceType(face.Type, index, projectTypeCode);
  return {
    dataURL: face.BackgroundUrl,
    dimensions: { height: face.Dimensions.Height, width: face.Dimensions.Width },
    id: String(face.FaceId),
    label: getSurfaceLabel(type),
    navigationLabel: getSurfaceLabel(type),
    type,
    trimTransparentEdges: projectTypeCode === CardType.SAS || undefined,
  };
};

/**
 * Narrows optional surface results after filtering unavailable faces.
 *
 * @param value Candidate preview surface
 * @returns True when a surface was created
 */
const isSurface = (value: CardPreviewSurface | undefined): value is CardPreviewSurface => Boolean(value);

/**
 * Builds quick-preview state from the current editor canvases.
 *
 * `createCardPreviewState` owns data assembly: choosing live canvases vs.
 * template fallbacks, normalizing panel labels, and attaching renderer metadata.
 * `createPreviewSnapshot` owns the Fabric-specific image capture. Keeping them
 * separate lets stories/tests build preview state without knowing about Fabric,
 * and lets snapshot behavior stay isolated and non-destructive.
 *
 * Integration guidance:
 * - Treat this as an on-demand snapshot operation. Generate when the user opens
 *   or switches into Quick Preview, not on every text/image edit.
 * - If the preview stays open while editing continues, debounce regeneration or
 *   refresh on explicit preview re-entry. `toDataURL()` is visually accurate but
 *   expensive enough that it should not run on every keystroke.
 * - Keep the returned dataURLs in memory only. They are large and should not be
 *   persisted to project JSON, localStorage, logs, or API payloads.
 * - Storybook/template fallbacks are layout demos only. The real text/photo
 *   preview path requires live Fabric canvases from `cardFacesList`.
 *
 * @param options Project data and optional live editor faces
 * @returns Serializable quick-preview state consumed by CardPreview
 */
export const createCardPreviewState = ({
  cardFacesList,
  project,
  faceVisibility = 'preview',
}: CreateCardPreviewStateOptions): CardPreviewState => {
  const projectTypeCode = project?.project_type_code;
  const liveSurfaces =
    cardFacesList
      ?.map((face, index) =>
        (faceVisibility === 'editor' ? face.editorDisplayIndicator : face.previewDisplayIndicator !== false)
          ? createSurfaceFromCardFace(face, index, projectTypeCode)
          : undefined,
      )
      .filter(isSurface) ?? [];

  if (liveSurfaces.length > 0) {
    return {
      id: project?.project_id,
      name: project?.name,
      surfaces: liveSurfaces,
    };
  }

  const templateSurfaces =
    project?.variables.template_data.Faces.map((face, index) =>
      (faceVisibility === 'editor' ? face.DisplayIndicator : face.PreviewDisplayIndicator !== false)
        ? createSurfaceFromTemplateFace(face, index, projectTypeCode)
        : undefined,
    ).filter(isSurface) ?? [];

  return {
    id: project?.project_id,
    name: project?.name,
    surfaces: templateSurfaces,
  };
};
